import { MAX_5_CHARACTERS_MESSAGE, REQUIRED_MESSAGE } from "@/utils/constant";
import { axiosInstance } from "@/utils/lib/axios";
import {
  QueryClient,
  QueryFunctionContext,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { LoaderFunction, LoaderFunctionArgs } from "react-router-dom";
import { z } from "zod";
import { Paginated } from "./types";
import { generateParams } from "@/utils/helper/generateParams";
import { DataConfig } from "@/components/table/useDefaultDataConfig";
import { stringRange } from "@/utils/helper/refinement";
import { AxiosResponse } from "axios";

const provincePayloadSchema = z.object({
  provinceCode: z
    .string()
    .max(5, { message: MAX_5_CHARACTERS_MESSAGE })
    .nullable()
    .optional(),
  provinceName: z
    .string()
    .min(1, { message: REQUIRED_MESSAGE })
    .refine(...stringRange(4, 50)),
});

type ProvincePayloadSchema = z.infer<typeof provincePayloadSchema>;

type ProvinceData = {
  id: string;
  provinceCode?: string | null;
  provinceName: string;
};

/* ============= *
 * READ province *
 * ============= */

type ProvincesQueryKey = DataConfig & {
  provinceName?: string;
};
interface ProvinceList extends Paginated {
  content: ProvinceData[];
}

async function getProvinces(
  queryFunctionContext: QueryFunctionContext,
): Promise<ProvinceList> {
  const { queryKey } = queryFunctionContext;
  const [_, provincesQueryKey] = queryKey;
  const params = generateParams(provincesQueryKey as ProvincesQueryKey);

  const response = await axiosInstance.get("/api/crud/province" + params);
  return response.data.data;
}

function useProvinces(
  provincesQueryKey: ProvincesQueryKey = {},
  options?: {
    enabled?: boolean;
  },
) {
  return useQuery({
    ...options,
    queryKey: ["provinces", provincesQueryKey],
    queryFn: getProvinces,
  });
}

interface ProvinceQueryKey {
  id: string;
}

async function getProvince(
  queryFunctionContext: QueryFunctionContext,
): Promise<ProvinceData> {
  const { queryKey } = queryFunctionContext;
  const [_, provinceQueryKey] = queryKey;
  const { id } = provinceQueryKey as ProvinceQueryKey;

  const response = await axiosInstance.get(`/api/crud/province/${id}`);
  return response.data.data;
}

function useProvince(provinceQueryKey: ProvinceQueryKey) {
  const { id } = provinceQueryKey;
  return useQuery({
    queryKey: ["province", { id }],
    queryFn: getProvince,
  });
}

function provinceLoader(
  queryClient: QueryClient,
): LoaderFunction<ProvinceData> {
  return async ({ params }: LoaderFunctionArgs<ProvinceData>) => {
    const province = await queryClient.ensureQueryData({
      queryKey: ["province", { id: params.id }],
      queryFn: getProvince,
    });
    return province;
  };
}

/* =============== *
 * CREATE province *
 * =============== */

async function createProvince(
  data: ProvincePayloadSchema,
): Promise<ProvinceData> {
  const response = await axiosInstance.post("/api/crud/province", data);
  return response.data;
}

function useProvinceCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: ProvincePayloadSchema }) =>
      createProvince(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["provinces"] });
    },
  });
}

/* =============== *
 * UPDATE province *
 * =============== */

async function editProvince(id: string, data: ProvincePayloadSchema) {
  const response = await axiosInstance.post(`/api/crud/province/${id}`, data);
  return response.data;
}

function useProvinceEdit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ProvincePayloadSchema }) =>
      editProvince(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["provinces"] });
    },
  });
}

/* =============== *
 * DELETE province *
 * =============== */

async function deleteProvince(id: string) {
  const response = await axiosInstance.delete(`/api/crud/province/${id}`);
  return response.data;
}

function useProvinceDelete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => deleteProvince(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["provinces"] });
    },
  });
}

async function deleteBulkProvince(ids: string[]) {
  return await Promise.all(ids.map((id) => deleteProvince(id)));
}

function useProvinceBulkDelete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ids }: { ids: string[] }) => deleteBulkProvince(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["provinces"] });
    },
  });
}

// Utility to poll for media completion
async function pollMediaStatus(
  id: string,
): Promise<{ status: string; path?: string; originalFileName?: string }> {
  const response: AxiosResponse = await axiosInstance.get(
    `/api/crud/media/${id}`,
  );
  return response.data.data;
}

// Wait for media completion with a polling mechanism
async function waitForMediaCompletion(
  id: string,
  interval = 2000,
  pollFn = pollMediaStatus, // Dependency injection for easier testing
): Promise<{ status: string; path?: string; originalFileName?: string }> {
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { status, path, originalFileName } = await pollFn(id);

    if (status === "SUCCESS" || status === "ERROR") {
      return { status, path, originalFileName: originalFileName };
    }

    await new Promise((resolve) => setTimeout(resolve, interval));
  }
}

// Utility to handle file download
function downloadFile(
  blobData: Blob,
  fileName: string,
  createObjectURL = window.URL.createObjectURL,
): void {
  const url = createObjectURL(blobData);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
}

// Export province data
async function exportProvince(
  config: DataConfig = {},
  waitFn = waitForMediaCompletion, // Dependency injection for easier testing
  downloadFn = downloadFile, // Dependency injection for easier testing
): Promise<{ status: string }> {
  const createResponse = await axiosInstance.get("/api/crud/province/excel", {
    params: { ...config },
  });

  const { status, path, originalFileName } = await waitFn(
    createResponse.data.data.id,
  );

  if (status === "SUCCESS") {
    const downloadResponse = await axiosInstance.get(
      "/api/crud/media/download",
      {
        params: { path, fileName: originalFileName },
        responseType: "blob",
      },
    );

    downloadFn(
      new Blob([downloadResponse.data]),
      originalFileName ?? "province-list.xlsx",
    );
  }

  return { status };
}

// React Query hook for exporting province
function useExportProvince() {
  return useMutation({
    mutationFn: async ({ config }: { config?: DataConfig }) =>
      exportProvince(config),
  });
}

export type { ProvinceData, ProvincePayloadSchema };
export {
  provincePayloadSchema,
  provinceLoader,
  useProvinces,
  useProvince,
  useProvinceCreate,
  useProvinceEdit,
  useProvinceDelete,
  useProvinceBulkDelete,
  pollMediaStatus,
  waitForMediaCompletion,
  downloadFile,
  exportProvince,
  useExportProvince,
};
